
import { computed, defineComponent, watch } from 'vue';
import Modal from '@/components/Modal.vue';
import { useBatch, useClaims, useTaxonomy, useToast } from '@/hooks';
import { IBatch } from '@/types';
import { CLAIM_STATUS, claimQueryKey } from '@/data';
import { throttle } from 'lodash';
import { injectOrThrow } from '@/utils';
import { Filter, FILTER_KEY } from '@/hooks/useFilter';

export default defineComponent({
  name: 'ModalSubmitBatch',
  components: {
    Modal,
  },
  props: {
    batchCode: {
      type: String,
      required: true,
    },
    supplierId: {
      type: Number,
      required: true,
    },
  },
  emits: ['close', 'complete'],
  setup(props, { emit }) {
    /**
     * Filter state; provided in Home.vue
     */
    const _filter: Filter = injectOrThrow(FILTER_KEY);

    const { isSubmittingBatch, submitBatchSuccess, submitBatchBySupplier } = useBatch(
      undefined,
      props.supplierId
    );
    const { getClaimsStatusByFilter } = useClaims();
    const { toggleToast } = useToast();
    const { getSupplierName } = useTaxonomy();

    const supplierName = getSupplierName(props.supplierId);

    /**
     * Lock the UI while the API is submitting,
     * and keep it locked once the API returns successfully, until the Modal closes itself
     */
    const isUiLocked = computed(() => {
      return isSubmittingBatch.value || submitBatchSuccess.value === true;
    });

    const closeModal = () => {
      emit('close');
    };

    const _submitBatch = throttle(() => {
      const payload: IBatch = {
        status: CLAIM_STATUS.QA_COMPLETE,
        [claimQueryKey.supplier]: props.supplierId,
        url: `${process.env.VUE_APP_PUBLIC_URL}/${encodeURIComponent('#')}/gallery`,
        // eslint-disable-next-line @typescript-eslint/camelcase
        [claimQueryKey.isUpdatedByCurrentUser]: true,
      };

      if (props.batchCode) {
        payload.batchCode = props.batchCode;
        delete payload.status;
      }

      submitBatchBySupplier(payload);
    }, 100);

    const onSubmitBatch = () => {
      if (!!props.supplierId && !isUiLocked.value) {
        _submitBatch();
      }
    };

    watch(submitBatchSuccess, () => {
      if (submitBatchSuccess.value) {
        getClaimsStatusByFilter(_filter.toLocationQuery());
        toggleToast(true, 'success', `Successfully submitted batch for ${supplierName}`);
        closeModal();
        emit('complete');
      }
    });

    return {
      closeModal,
      isSubmittingBatch,
      isUiLocked,
      onSubmitBatch,
      supplierName,
    };
  },
});
